import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Package, Calendar, Download, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SelfUseAudit() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales/self-use`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setItems(res.data.items || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      toast.error("Failed to load self-use data");
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = items.filter(item =>
    !searchTerm ||
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.plu_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.transaction_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportCSV = () => {
    if (!filtered.length) return;
    const headers = ["Date", "Transaction", "PLU", "Product", "Qty", "Cost Price", "Total Cost", "Cashier"];
    const rows = filtered.map(i => [
      i.date, i.transaction_number, i.plu_code, i.name,
      i.quantity, i.cost_price?.toFixed(2), i.total_cost?.toFixed(2), i.cashier
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `self-use-audit-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4" data-testid="self-use-audit-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Package className="h-5 w-5 text-teal-600" />
          Self-Use Audit Trail
        </h1>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={!filtered.length} data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Transactions</p>
              <p className="text-lg font-bold" data-testid="summary-transactions">{summary.total_transactions}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Items Used</p>
              <p className="text-lg font-bold" data-testid="summary-items">{summary.total_items}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Total Cost Value</p>
              <p className="text-lg font-bold text-teal-700" data-testid="summary-cost">R {summary.total_cost_value?.toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <label className="text-xs text-gray-500 block mb-1">From</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-sm w-36" data-testid="start-date" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">To</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-sm w-36" data-testid="end-date" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">Search</label>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-gray-400" />
                <Input
                  placeholder="Search product, PLU or transaction..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="h-8 text-sm pl-7"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Button size="sm" onClick={fetchData} className="h-8 bg-teal-600 hover:bg-teal-700" data-testid="refresh-btn">
              <Calendar className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="self-use-table">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 border-b">
                  <th className="p-2">Date</th>
                  <th className="p-2">Transaction</th>
                  <th className="p-2">PLU</th>
                  <th className="p-2">Product</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2 text-right">Cost Price</th>
                  <th className="p-2 text-right">Total Cost</th>
                  <th className="p-2">Cashier</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-6 text-center text-gray-400">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="p-6 text-center text-gray-400">No self-use items found for this period</td></tr>
                ) : (
                  filtered.map((item, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50" data-testid={`self-use-row-${idx}`}>
                      <td className="p-2 text-xs">{item.date}</td>
                      <td className="p-2 text-xs font-mono">{item.transaction_number}</td>
                      <td className="p-2 text-xs">{item.plu_code}</td>
                      <td className="p-2 text-xs font-medium">{item.name}</td>
                      <td className="p-2 text-xs text-right">{item.quantity}</td>
                      <td className="p-2 text-xs text-right">R {item.cost_price?.toFixed(2)}</td>
                      <td className="p-2 text-xs text-right font-medium text-teal-700">R {item.total_cost?.toFixed(2)}</td>
                      <td className="p-2 text-xs">{item.cashier}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 font-semibold text-xs border-t-2">
                    <td colSpan={4} className="p-2">TOTAL</td>
                    <td className="p-2 text-right">{filtered.reduce((s, i) => s + (i.quantity || 0), 0)}</td>
                    <td className="p-2"></td>
                    <td className="p-2 text-right text-teal-700">
                      R {filtered.reduce((s, i) => s + (i.total_cost || 0), 0).toFixed(2)}
                    </td>
                    <td className="p-2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
